import {
  SISTER_SEARCHER_SIMILARITY_OPTIONS,
  SISTER_SEARCHER_YEAR_OPTIONS,
} from "../constants/sister-searcher.constants";
import {
  SisterSearcherInImosFilter,
  SisterSearcherSimilarityCriteria,
  SisterSearcherSimilarityKey,
} from "../models/sister-searcher.models";
import { CheckIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

interface SisterSearcherFiltersProps {
  allSimilarityOptionsSelected: boolean;
  inImos: SisterSearcherInImosFilter;
  onInImosChange: (selection: SisterSearcherInImosFilter) => void;
  onToggleAllSimilarity: () => void;
  onToggleSimilarity: (key: SisterSearcherSimilarityKey) => void;
  onYearsAfterChange: (years: number) => void;
  onYearsBeforeChange: (years: number) => void;
  similarity: Omit<SisterSearcherSimilarityCriteria, "inImos">;
}

// Presents the PBIP similarity switches as independent, keyboard-accessible controls.
// IMOS uses the report's single-select slicer semantics, including its explicit Select all choice.
const SisterSearcherFilters = ({
  allSimilarityOptionsSelected,
  inImos,
  onInImosChange,
  onToggleAllSimilarity,
  onToggleSimilarity,
  onYearsAfterChange,
  onYearsBeforeChange,
  similarity,
}: SisterSearcherFiltersProps) => (
  <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_290px_170px]">
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <InformationCircleIcon className="h-4 w-4 text-green" />
          <span>Selected similarity filters are highlighted.</span>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
          <button
            type="button"
            aria-pressed={allSimilarityOptionsSelected}
            onClick={onToggleAllSimilarity}
            className={twMerge(
              "flex min-h-[54px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-center text-xs font-semibold transition-colors",
              allSimilarityOptionsSelected
                ? "border-green bg-green text-white"
                : "border-gray-300 bg-white text-gray-600 hover:border-green hover:text-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300",
            )}
            data-test="sister-searcher-select-all-similarity-toggle"
          >
            {allSimilarityOptionsSelected && <CheckIcon className="h-4 w-4 flex-none" />}
            Select all
          </button>
          {SISTER_SEARCHER_SIMILARITY_OPTIONS.map((option) => {
            const active = similarity[option.key];

            return (
              <button
                key={option.key}
                type="button"
                aria-pressed={active}
                onClick={() => onToggleSimilarity(option.key)}
                className={twMerge(
                  "flex min-h-[54px] items-center justify-center gap-2 rounded-lg border px-3 py-2 text-center text-xs font-semibold transition-colors",
                  active
                    ? "border-green bg-green text-white"
                    : "border-gray-300 bg-white text-gray-600 hover:border-green hover:text-green dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300",
                )}
                data-test={`sister-searcher-${option.key}-toggle`}
              >
                {active && <CheckIcon className="h-4 w-4 flex-none" />}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
        <label>
          <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-300">Years Before</span>
          <select
            value={similarity.yearsBefore}
            onChange={(event) => onYearsBeforeChange(Number(event.target.value))}
            className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs dark:border-gray-600 dark:bg-gray-700"
            data-test="sister-searcher-years-before-select"
          >
            {SISTER_SEARCHER_YEAR_OPTIONS.map((years) => <option key={years}>{years}</option>)}
          </select>
        </label>
        <label>
          <span className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-300">Years After</span>
          <select
            value={similarity.yearsAfter}
            onChange={(event) => onYearsAfterChange(Number(event.target.value))}
            className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-xs dark:border-gray-600 dark:bg-gray-700"
            data-test="sister-searcher-years-after-select"
          >
            {SISTER_SEARCHER_YEAR_OPTIONS.map((years) => <option key={years}>{years}</option>)}
          </select>
        </label>
      </div>

      <fieldset className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
        <legend className="px-1 text-xs font-medium text-gray-600 dark:text-gray-300">In IMOS</legend>
        <div className="space-y-2 pt-1">
          {(["all", "no", "yes"] as SisterSearcherInImosFilter[]).map((option) => (
            <label key={option} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
              <input
                type="radio"
                name="sister-searcher-in-imos"
                checked={inImos === option}
                onChange={() => onInImosChange(option)}
                className="h-4 w-4 text-green focus:ring-green"
                data-test={`sister-searcher-in-imos-${option}-radio`}
              />
              {option === "all" ? "Select all" : option === "yes" ? "Yes" : "No"}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  </section>
);

export default SisterSearcherFilters;
